'use client';

import React from 'react';

export interface BadgeProps {
  /** `premium` = champagne, reserved for locked Pro/Premium tiers only. */
  tone?: 'neutral' | 'brand' | 'success' | 'warn' | 'error' | 'info' | 'premium';
  /** Leading status dot — pair with a label so state is never color-alone. */
  dot?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * Status / metadata badge. State = color + label + optional dot
 * (never color alone). `premium` uses champagne — reserved for locked tiers.
 */
export function Badge({
  tone = 'neutral',
  dot = false,
  children,
  style = {},
  ...rest
}: BadgeProps & Omit<React.HTMLAttributes<HTMLSpanElement>, keyof BadgeProps>) {
  const tones = {
    neutral: {
      background: 'var(--bg-sunken)',
      color: 'var(--text-secondary)',
      dot: 'var(--text-tertiary)',
    },
    brand: {
      background: 'var(--brand-wash)',
      color: 'var(--accent-deep)',
      dot: 'var(--brand)',
    },
    success: {
      background: 'var(--success-wash)',
      color: 'var(--success)',
      dot: 'var(--success)',
    },
    warn: {
      background: 'var(--warn-wash)',
      color: 'var(--warn)',
      dot: 'var(--warn)',
    },
    error: {
      background: 'var(--error-wash)',
      color: 'var(--error)',
      dot: 'var(--error)',
    },
    info: {
      background: 'var(--info-wash)',
      color: 'var(--info)',
      dot: 'var(--info)',
    },
    premium: {
      background: 'var(--champagne-wash)',
      color: 'var(--champagne-500)',
      dot: 'var(--champagne-500)',
    },
  }[tone];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        paddingInline: 'var(--sp-3)',
        height: 24,
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-micro)',
        fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
        borderRadius: 'var(--r-full)',
        background: tones.background,
        color: tones.color,
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 'var(--r-full)',
            background: tones.dot,
            flex: '0 0 auto',
          }}
        />
      )}
      {children}
    </span>
  );
}
